import Vector from "victor";
import renderAndDownloadImage from "@/utils/renderAndDownloadImage";
import { loadImage } from "./ImageUploadController";
import { LineShape2 } from "./standardShapes/StandardShapes";
import { ShapeType } from "@/wbcontroller/types/ShapeType";
import WhiteboardControllerInterface from "./types/WhiteboardControllerInterface";
import { Store } from "vuex";
import { AllState } from "@/store/AllState";
import { ToolConfigMutations } from "@/store/modules/toolConfig";
import { Library, replaceLibraries } from "@/library";

interface DownloadImageMessage {
  action: "downloadImage";
  transparentBackground?: boolean;
  scaleFactor?: number;
  progressCallback?: (progress: number) => void;
  finishCallback: (url: string) => void;
}

interface ExportLineJSONMessage {
  action: "exportLineJSON";
}

interface InsertImageMessage {
  action: "insertImage";
  src?: string;
  url: string;
}

interface InsertPDFMessage {
  action: "insertPDF";
  src?: string;
  url: string;
}

interface SetCustomColorsMessage {
  action: "setCustomColors";
  colors: [string, string[]][];
}

interface SetStampsMessage {
  action: "setStamps";
  stamps: [Library];
}

type IframeMessage =
  | DownloadImageMessage
  | ExportLineJSONMessage
  | InsertImageMessage
  | InsertPDFMessage
  | SetCustomColorsMessage
  | SetStampsMessage;

// Does not support scaled lines!
function getLinePoints(s: LineShape2): {
  a: [number, number];
  b: [number, number];
  strokeWidth: number;
  strokeColor: string;
} {
  function addPairs(
    aa: [number, number],
    bb: [number, number],
    cc: [number, number]
  ): [number, number] {
    return [aa[0] + bb[0] + cc[0], aa[1] + bb[1] + cc[1]];
  }

  return {
    a: addPairs(s.pointA, s.boundingBoxOrigin, s.translate),
    b: addPairs(s.pointB, s.boundingBoxOrigin, s.translate),
    strokeWidth: s.strokeWidth,
    strokeColor: s.strokeColor,
  };
}

export default function handleIframeMessage(
  e: MessageEvent,
  wb: WhiteboardControllerInterface,
  store: Store<AllState>
) {
  const data = e.data as IframeMessage;
  let lines: unknown[] = [];
  switch (data.action) {
    case "downloadImage":
      renderAndDownloadImage(
        wb,
        data.scaleFactor || 1,
        data.transparentBackground || true,
        (progress) => {
          window.top?.postMessage(
            { action: "updateDownloadImageProgress", progress },
            "*"
          );
        },
        (url) => {
          window.top?.postMessage(
            { action: "downloadImageComplete", url },
            "*"
          );
        }
      );
      break;
    case "insertImage":
      loadImage(data.src || data.url).then((img) => {
        wb.insertImages([
          {
            url: data.src || data.url,
            size: new Vector(img.width, img.height),
            position: wb.camera.origin,
          },
        ]);
      });
      break;
    case "insertPDF":
      wb.insertPDF(data.src || data.url);
      break;
    case "exportLineJSON":
      lines = wb.drawing.orderedShapes
        .filter((s) => s.shape.shapeType === ShapeType.Line)
        .map((s) => s.shape as LineShape2)
        .map(getLinePoints);
      window.top?.postMessage(
        {
          action: "exportLineJSON",
          lines,
        },
        "*"
      );
      break;
    case "setCustomColors":
      ToolConfigMutations.configureCustomColors(store.commit, data.colors);
      break;
    case "setStamps":
      replaceLibraries(data.stamps);
      break;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).handleIframeMessage = handleIframeMessage;
